/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from '@studio-freight/lenis';
import { LANGUAGE_CHOICE_KEY, localizePath } from './src/utils/languagePath';

// Register GSAP plugins
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// Open English pages in Spanish when the browser's main language is Spanish,
// unless the visitor already picked a language with the toggle this visit.
// Returns true when it starts a redirect.
const redirectToBrowserLanguage = () => {
  try {
    const { pathname, search, hash } = window.location;
    if (/^\/(es-mx|pt-br)(\/|$)/.test(pathname)) return false;
    if (window.sessionStorage.getItem(LANGUAGE_CHOICE_KEY)) return false;

    const browserLang = ((navigator.languages && navigator.languages[0]) || navigator.language || '').toLowerCase();
    if (!browserLang.startsWith('es')) return false;

    window.location.replace(localizePath(pathname, 'es-mx') + search + hash);
    return true;
  } catch (e) {
    return false;
  }
};

// Initialize Lenis smooth scroll
export const onClientEntry = () => {
  if (redirectToBrowserLanguage()) return;

  // Initialize Lenis smooth scroll
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // easeOutExpo
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    smoothWheel: true,
    wheelMultiplier: 1,
    smoothTouch: false,
    touchMultiplier: 2,
    infinite: false,
  });

  // Connect Lenis with GSAP ScrollTrigger
  lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });

  gsap.ticker.lagSmoothing(0);

  // Request animation frame for Lenis
  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }

  requestAnimationFrame(raf);

  // Store lenis instance globally for access in components
  if (typeof window !== 'undefined') {
    window.lenis = lenis;
  }

  console.log('✨ GSAP ScrollTrigger and Lenis smooth scroll initialized');
};

// Cleanup on route change
export const onRouteUpdate = ({ location, prevLocation }) => {
  // Scroll to top on route change
  if (typeof window !== 'undefined' && window.lenis) {
    window.lenis.scrollTo(0, { immediate: true });
  }

  // Update ScrollTrigger on route change
  if (typeof window !== 'undefined') {
    ScrollTrigger.refresh();
  }
};

// Prevent scroll restoration (let Lenis handle it)
export const shouldUpdateScroll = () => {
  return false;
};
